import { useLocation } from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import {
  Typography,
  Divider,
  Button, 
  Icon,
  Box
} from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import ShareDialog from "src/components/blocks/dialogs/ShareDialog.js";
import RecommendedCalendars from "src/components/blocks/RecommendedCalendars.js";
import InstructionsLayout from "../InstructionsLayout.js";
import { getPageRoute } from "src/services";
import { UpgradeConsumerButton } from "src/components/upgrades/consumer/UpgradeConsumerButton.js";
import { PlanComparisonTable } from "src/components/upgrades/PlanComparisonTable.js";
import { consumerPlanData } from "src/components/upgrades/consumer/ConsumerPlanData.js";
import BenefitsCarousel from "src/components/upgrades/BenefitsCarousel.js";
import { UpgradeCTA } from "src/components/upgrades/UpgradeCTA.js";
import confetti from 'canvas-confetti';
import { customEvent } from "src/utils/gtag.js";
import { useTheme } from "@emotion/react";


const SuccessfulCalendarSubscription = () => {
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const [stars, setStars] = useState(0);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const theme = useTheme();
  const isLifeTimeMember = user?.lifetimeMember;

  
  const sourceCalendar = location.state ? location.state.calendar ?? null : null;
  const isReminder = location.state?.isReminder ?? false;

  console.log(isReminder);

  const boomTitle = (
    <span style={{ fontWeight: 400 }}>
      <span style={{ fontWeight: 700, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Boom!</span> <br /> You're all set.
    </span>
  );

  const description = isReminder 
    ? `Follow the instructions in the reminder to subscribe to "${sourceCalendar?.name}" on your desktop, then sync it with the Google Calendar app on your phone.`
    : (
        <>
          Your calendar will now <b>automatically update</b> whenever <b>`{sourceCalendar?.name}`</b> events are added, changed, or cancelled.
        </>
      );

  useEffect(() => {
    const count = 200;
    const defaults = {
      origin: { y: 0.7 },
      zIndex: 1500,
      colors: ['#000000', '#FFFFFF'], // Black and white colors
    };

    function fire(particleRatio, opts) {
      confetti({
        ...defaults,
        ...opts,
        particleCount: Math.floor(count * particleRatio)
      });
    }

    // First burst - black and white mix
    fire(0.4, {
      spread: 100,
      startVelocity: 55,
      scalar: 0.8,
      colors: ['#000000', '#FFFFFF']
    });

    // Second burst - mostly white
    fire(0.3, {
      spread: 60,
      startVelocity: 45,
      colors: ['#FFFFFF']
    });

    // Third burst - mostly black
    fire(0.3, {
      spread: 80,
      startVelocity: 35,
      colors: ['#000000']
    });

    return () => confetti.reset();
  }, []);

  useEffect(() => {
    customEvent({
      name: "landed_on_subscription_success",
      category: "CalendarSubscription",
      label: "Landed on Calendar Subscription Success Page",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        calendar_handle: sourceCalendar?.handle,
      }
    });
  }, [stars]);

  return (
    <InstructionsLayout nav="home" sx={{ textAlign: "center" }}>
      {/* Hero Section */}
      <Box sx={{ mb: 6, textAlign: "left" }}>
        <Typography 
          variant="h2" 
          sx={{ 
            mb: 3,
            fontSize: { xs: '2.5rem', md: '3.5rem' },
            fontWeight: 400,
            lineHeight: 1.2,
            letterSpacing: '-0.02em'
          }}
        >
          {boomTitle}
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: "text.primary", 
            mb: 5,
            maxWidth: '500px',
            lineHeight: 1.6,
            fontSize: '1.1rem',
            fontWeight: 400
          }}
        >
          {description}
        </Typography>

        {/* Action Buttons */}
        <Button 
          variant="contained" 
          fullWidth
          onClick={() => setShareDialogOpen(true)}
          sx={{ mb: 2 }}
        >
          Share
        </Button>
        <Button 
          variant="outlined" 
          fullWidth
          href={getPageRoute("home", "HOME", {}, { tab: user?.tags?.length > 0 ? "foryou" : "discover" })}
        >
          Explore Library
        </Button>
      </Box>

      <ShareDialog
        calendar={sourceCalendar}
        handle={sourceCalendar?.handle}
        open={shareDialogOpen}
        setOpen={setShareDialogOpen}
        as="Hidden"
      />

      {/* Minimal Divider */}
      <Box sx={{ display: 'flex', alignItems: 'center', my: 6 }}>
        <Box sx={{ flex: 1, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.08) 50%, transparent 100%)' }} />
      </Box>

      {/* Problem Statement Section */}
      <Box sx={{ mb: 8, textAlign: "left" }}>
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 4,
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.02em'
          }}
        >
          Don't let your calendar <br />
          <Box component="span" sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            keep you guessing.
          </Box>
        </Typography>
        <Typography 
          variant="h6" 
          sx={{ 
            color: "text.primary", 
            mb: 6, 
            maxWidth: "500px", 
            lineHeight: 1.6,
            fontSize: '1.1rem',
            fontWeight: 400
          }}
        >
          All calendar apps update silently. <br /><br />Updates can take up to <b>48 hours</b> and you <b>won't know</b> when your calendar last updated. 
        </Typography>
        <BenefitsCarousel variant="dark" sx={{ mb: 6 }} />
      </Box>

      {/* Upgrade Section */}
      <Box sx={{ mb: 8 }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "0 auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "4rem",
            mb: 3,
            fontWeight: 300,
            background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          rocket_launch
        </Icon>
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 6,
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.02em'
          }}
        >
          Go Plus. <Box component="span" sx={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Go Big.</Box>
        </Typography>
        <PlanComparisonTable 
          sx={{ my: 6 }}
          data={consumerPlanData}
        />
        <UpgradeCTA variant="dark" />
      </Box>

      {/* Discovery Section */}
      <Box sx={{ mb: 6 }}>
        <Icon
          baseClassName="material-symbols-outlined"
          sx={{
            display: "block",
            margin: "0 auto",
            color: `${theme.palette.icon.primary}`,
            verticalAlign: "middle",
            fontSize: "4rem",
            mb: 3,
            fontWeight: 300,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          lock_open
        </Icon>
        <Typography 
          variant="h3" 
          sx={{ 
            mb: 6,
            fontSize: { xs: '2rem', md: '2.5rem' },
            fontWeight: 600,
            lineHeight: 1.3,
            letterSpacing: '-0.02em'
          }}
        >
          Go Plus. <Box component="span" sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Go Unlimited</Box>.
        </Typography>
        
        {sourceCalendar && (
          <Box sx={{ mb: 6 }}>
            <RecommendedCalendars 
              sourceCalendars={[sourceCalendar]} 
              displayMode="related"
              sx={{ mt: 2 }}
            />
          </Box>
        )}

        <Button 
          variant="contained" 
          size="large" 
          fullWidth
          href={getPageRoute("home", "HOME", {}, { tab: user?.tags?.length > 0 ? "foryou" : "discover" })}
          sx={{ mb: 4 }}
        >
          Discover More Calendars
        </Button>
      </Box>
    </InstructionsLayout>
  );
};

export default SuccessfulCalendarSubscription;